/**
 * Значки ANZONIX — вместо эмодзи.
 *
 * Эмодзи рисует система, и на каждой он свой: на Windows один, на
 * телефоне другой, и ни один не в нашем стиле. Эти — тонкой линией со
 * скруглёнными концами; цвет и свечение задаёт место, где значок стоит
 * (`currentColor`). Один набор на студию, карточку индикатора и ботов.
 */

import type { JSX } from 'react';

export type IconName =
  | 'bot'
  | 'sparkle'
  | 'studio'
  | 'puzzle'
  | 'chat'
  | 'library'
  | 'bell'
  | 'bell-off'
  | 'eye'
  | 'eye-off'
  | 'trash'
  | 'edit'
  | 'star'
  | 'trend'
  | 'reversal'
  | 'breakout'
  | 'classic'
  | 'crypto'
  | 'smart-money'
  | 'ict'
  | 'volume'
  | 'price-action'
  | 'indicators'
  | 'scalping'
  | 'range'
  | 'multi-tf'
  | 'momentum'
  | 'volatility'
  | 'structure'
  | 'liquidity'
  | 'zone'
  | 'candle'
  | 'price'
  | 'target'
  | 'globe'
  | 'scales'
  | 'wand'
  | 'compass'
  | 'map'
  | 'history'
  | 'lock'
  | 'field'
  | 'flask'
  | 'code';

const PATHS: Record<IconName, JSX.Element> = {
  bot: (
    <>
      <rect x="4" y="8" width="16" height="11" rx="4" />
      <path d="M12 4v4M9 13h.01M15 13h.01M9.5 16.2h5M2 12.5v2M22 12.5v2" />
      <circle cx="12" cy="3.5" r="1" />
    </>
  ),
  sparkle: (
    <>
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
      <path d="M19 15.5l.8 1.7 1.7.8-1.7.8-.8 1.7-.8-1.7-1.7-.8 1.7-.8z" />
    </>
  ),
  /*
   * Знак студии индикаторов — призма: рынок входит одним лучом, выходят
   * два сигнала, покупка и продажа. Ровно то, что делает индикатор.
   */
  studio: (
    <>
      <path d="M9.6 3.8l7 14.6H2.6z" />
      <path d="M0.9 12.3h5.3" />
      <path d="M12.9 10.9l6.6-3.1" />
      <path d="M14.4 14l5.1 2.6" />
      <circle cx="20.9" cy="7.2" r="1.5" fill="currentColor" />
      <circle cx="20.9" cy="17.3" r="1.5" fill="currentColor" />
    </>
  ),
  puzzle: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="2" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="2" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="2" />
      <path d="M17 13.5v7M13.5 17h7" />
    </>
  ),
  chat: (
    <>
      <path d="M5 5h14a2 2 0 012 2v8a2 2 0 01-2 2h-7l-5 4v-4H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
      <path d="M8 10h8M8 13h5" />
    </>
  ),
  library: (
    <>
      <rect x="4" y="5" width="11" height="15" rx="2" />
      <path d="M8 3h9a3 3 0 013 3v11" />
      <path d="M7 15l2.5-3 2 1.5L14 10" />
    </>
  ),
  bell: (
    <>
      <path d="M6 16V11a6 6 0 0112 0v5l1.5 2h-15z" />
      <path d="M10 20.5a2.2 2.2 0 004 0" />
    </>
  ),
  'bell-off': (
    <>
      <path d="M8.5 6.2A6 6 0 0118 11v5l1.5 2H9M6 11v5l-1.5 2H7" />
      <path d="M10 20.5a2.2 2.2 0 004 0M3 3l18 18" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" />
      <circle cx="12" cy="12" r="3.2" />
    </>
  ),
  'eye-off': (
    <>
      <path d="M2 12s3.6-6.5 10-6.5c1.8 0 3.3.5 4.6 1.2M22 12s-3.6 6.5-10 6.5c-1.8 0-3.3-.5-4.6-1.2" />
      <path d="M4 20L20 4" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16M9 7V4.5h6V7M6.5 7l1 13h9l1-13" />
      <path d="M10 11v6M14 11v6" />
    </>
  ),
  edit: (
    <>
      <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="8" cy="17" r="2" />
    </>
  ),
  star: <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.8l-5.2 2.8 1-5.8-4.3-4.1 5.9-.9z" />,
  trend: (
    <>
      <path d="M3 18l6-6 4 3 7-8" />
      <path d="M15 7h5v5" />
    </>
  ),
  reversal: (
    <>
      <path d="M7 4v9a4 4 0 008 0V8" />
      <path d="M12 10l3-3 3 3M4 7l3-3 3 3" />
    </>
  ),
  breakout: (
    <>
      <path d="M3 9h18" strokeDasharray="2 3" />
      <path d="M4 18l5-5 3 2 8-10" />
      <path d="M16 5h4v4" />
    </>
  ),
  classic: (
    <>
      <path d="M8 4h8v5a4 4 0 01-8 0z" />
      <path d="M8 6H5a2 2 0 002 4M16 6h3a2 2 0 01-2 4M12 13v4M8.5 20h7M10 17h4" />
    </>
  ),
  crypto: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M10 8h3a2 2 0 010 4h-3zM10 12h3.5a2 2 0 010 4H10zM10 7v10M11.5 6v2M11.5 16v2" />
    </>
  ),
  'smart-money': (
    <>
      <path d="M12 3l8 5v8l-8 5-8-5V8z" />
      <path d="M12 8v8M8.5 10.5L12 8l3.5 2.5" />
    </>
  ),
  ict: (
    <>
      <path d="M3 20h18M5 20V10M9.7 20V10M14.3 20V10M19 20V10M2.5 10L12 4l9.5 6" />
    </>
  ),
  volume: <path d="M5 20V13M10 20V7M15 20v-9M20 20V4" />,
  'price-action': (
    <>
      <path d="M7 3v4M7 17v4M17 5v3M17 16v4" />
      <rect x="4.5" y="7" width="5" height="10" rx="1" />
      <rect x="14.5" y="8" width="5" height="8" rx="1" />
    </>
  ),
  indicators: <path d="M2 14c3 0 3-7 6-7s3 10 6 10 3-7 8-7" />,
  scalping: <path d="M13 2L4 14h7l-1 8 9-12h-7z" />,
  range: (
    <>
      <path d="M3 6h18M3 18h18" />
      <path d="M4 13l3-4 3 5 3-5 3 5 3-4" />
    </>
  ),
  'multi-tf': (
    <>
      <path d="M12 3l9 4.5-9 4.5-9-4.5z" />
      <path d="M3 12l9 4.5 9-4.5M3 16.5L12 21l9-4.5" />
    </>
  ),
  momentum: <path d="M13 2L4 14h7l-1 8 9-12h-7z" />,
  volatility: <path d="M2 12c2.5 0 2.5-6 5-6s2.5 12 5 12 2.5-12 5-12 2.5 6 5 6" />,
  structure: (
    <>
      <path d="M3 17l5-8 4 5 4-9 5 7" />
      <circle cx="16" cy="5" r="1.2" />
    </>
  ),
  liquidity: <path d="M12 3s6 6.5 6 11a6 6 0 01-12 0c0-4.5 6-11 6-11z" />,
  zone: (
    <>
      <rect x="3" y="8" width="18" height="8" rx="2" />
      <path d="M3 12h18" strokeDasharray="2 3" />
    </>
  ),
  candle: (
    <>
      <path d="M8 3v4M8 17v4M16 6v3M16 15v3" />
      <rect x="5.5" y="7" width="5" height="10" rx="1" />
      <rect x="13.5" y="9" width="5" height="6" rx="1" />
    </>
  ),
  price: (
    <>
      <path d="M3.5 12.5l8-8H20v8.5l-8 8z" />
      <circle cx="15.5" cy="8.5" r="1.4" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.6 2.4 3.8 5.2 3.8 8.5s-1.2 6.1-3.8 8.5c-2.6-2.4-3.8-5.2-3.8-8.5S9.4 5.9 12 3.5z" />
    </>
  ),
  scales: (
    <>
      <path d="M12 4v16M7 20h10M5 7h14M12 4.5l-7 2.5M12 4.5l7 2.5" />
      <path d="M5 7l-2.5 6a2.5 2.5 0 005 0zM19 7l-2.5 6a2.5 2.5 0 005 0z" />
    </>
  ),
  wand: (
    <>
      <path d="M4 20L15 9" />
      <path d="M13.5 7.5l3 3" />
      <path d="M18 3v3M16.5 4.5h3M20 9v2M19 10h2M9 3v2M8 4h2" />
    </>
  ),
  compass: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M15.5 8.5l-2 5-5 2 2-5z" />
    </>
  ),
  map: (
    <>
      <path d="M3.5 6.5l5-2.5 7 3 5-2.5v13l-5 2.5-7-3-5 2.5z" />
      <path d="M8.5 4v13M15.5 7v13" />
    </>
  ),
  history: (
    <>
      <path d="M4 12a8 8 0 102.3-5.7L4 8.5" />
      <path d="M4 4v4.5h4.5M12 8v4.5l3 2" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="9.5" rx="2.5" />
      <path d="M8 10.5V8a4 4 0 018 0v2.5" />
    </>
  ),
  field: (
    <>
      <path d="M3 20h18M3 4v16" />
      <path d="M7 15V11M11 13V7M15 16v-5M19 10V5" />
      <path d="M6 11h2M10 7h2M14 11h2M18 5h2" />
    </>
  ),
  flask: (
    <>
      <path d="M9.5 3.5h5M10.5 3.5v5.5L5 18.5a1.5 1.5 0 001.3 2h11.4a1.5 1.5 0 001.3-2L13.5 9V3.5" />
      <path d="M7.5 14.5h9" />
    </>
  ),
  /* Код ANZONIX: угловые скобки и курсор — «здесь пишут». */
  code: (
    <>
      <path d="M8 7l-5 5 5 5M16 7l5 5-5 5" />
      <path d="M13.5 4.5l-3 15" />
    </>
  ),
};

export function NeonIcon({ name, size = 22, className }: { name: IconName; size?: number; className?: string }): JSX.Element {
  return (
    <svg
      className={className ? `neon-icon ${className}` : 'neon-icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}

/** Цвет раздела идей: свой у каждого, чтобы библиотека читалась с первого взгляда. */
export const CATEGORY_TONE: Record<string, string> = {
  trend: '#2ee6b0',
  reversal: '#a78bfa',
  breakout: '#ff5470',
  classic: '#fbbf24',
  crypto: '#f7931a',
  'smart-money': '#67e8f9',
  ict: '#f5c04e',
  volume: '#60a5fa',
  'price-action': '#fb923c',
  indicators: '#c084fc',
  scalping: '#facc15',
  range: '#34d399',
  'multi-tf': '#38bdf8',
  momentum: '#facc15',
  volatility: '#60a5fa',
  structure: '#67e8f9',
  liquidity: '#38bdf8',
  zone: '#a78bfa',
  candle: '#fb923c',
  price: '#2ee6b0',
};
